/**
 * Campana de notificaciones del topbar. Dropdown con últimas 10 +
 * badge con count de no leídas. Polling cada 30s vía react-query.
 *
 * Fusión 2026-07-28 (Carlos): aquí se listan los RESULTADOS FINALES, incluidas
 * las tareas del navegador que antes tenían indicador propio en el topbar. El
 * trabajo EN CURSO se ve en el dock inferior (``BackgroundDock``): un control
 * para lo que terminó, otro para lo que está corriendo.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../api/notifications";
import type { NotificationItem, NotificationSeverity } from "../api/types";
import { formatRelative } from "../lib/format";
import { useBackgroundTasks, type BackgroundTask } from "./BackgroundTasks";
import { IconBell } from "./icons";

const POLL_MS = 30_000;

function severityColor(sev: NotificationSeverity): string {
  switch (sev) {
    case "success":
      return "#16a34a";
    case "warn":
      return "#d97706";
    case "error":
      return "#dc2626";
    default:
      return "#3b82f6";
  }
}

function estiloTarea(t: BackgroundTask): string {
  if (t.status === "error") return "#dc2626";
  return t.status === "done" ? "#16a34a" : "#3b82f6";
}

export function NotificationsBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Tareas del navegador (diagnóstico por HT, certificación…): sus resultados se
  // muestran aquí desde la fusión del indicador de background con la campana.
  const { tasks, activeCount } = useBackgroundTasks();

  const countQuery = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: getUnreadCount,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });

  const listQuery = useQuery({
    queryKey: ["notifications", "list", "dropdown"],
    queryFn: () => listNotifications({ limit: 10 }),
    enabled: open,
  });

  const readOne = useMutation({
    mutationFn: (id: number) => markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const readAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread = countQuery.data?.unread ?? 0;
  const finalizadas = tasks.filter((t) => t.status !== "running");

  const onClickItem = (n: NotificationItem) => {
    if (!n.read_at) readOne.mutate(n.id);
    setOpen(false);
    if (n.link_url) navigate(n.link_url);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="iconbtn"
        title={activeCount > 0
          ? `Notificaciones — ${activeCount} tarea(s) en curso`
          : "Notificaciones"}
        onClick={() => setOpen((v) => !v)}
        style={{ position: "relative" }}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <IconBell />
        {activeCount > 0 && unread === 0 && (
          <span
            aria-label={`${activeCount} tarea(s) en curso`}
            style={{
              position: "absolute", top: 4, right: 4, width: 8, height: 8,
              borderRadius: 4, background: "#3b82f6",
            }}
          />
        )}
        {unread > 0 && (
          <span
            aria-label={`${unread} no leídas`}
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 8,
              background: "#dc2626",
              color: "white",
              fontSize: 10,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notificaciones"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 6px)",
            width: 360,
            maxHeight: 480,
            overflowY: "auto",
            background: "var(--sar-surface)",
            border: "1px solid var(--sar-line)",
            borderRadius: 10,
            boxShadow: "var(--cs-shadow)",
            zIndex: 60,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 12px",
              borderBottom: "1px solid var(--sar-line)",
            }}
          >
            <strong style={{ fontSize: 13 }}>Notificaciones</strong>
            <div style={{ display: "flex", gap: 8, fontSize: 12 }}>
              <button
                type="button"
                onClick={() => readAll.mutate()}
                disabled={unread === 0 || readAll.isPending}
                style={{
                  background: "transparent",
                  border: "0",
                  color: "var(--sar-text-3)",
                  cursor: unread === 0 ? "default" : "pointer",
                }}
              >
                Marcar todas
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/notifications");
                }}
                style={{
                  background: "transparent",
                  border: "0",
                  color: "var(--sar-link)",
                  cursor: "pointer",
                }}
              >
                Ver todas
              </button>
            </div>
          </div>

          {finalizadas.length > 0 && (
            <div style={{ borderBottom: "1px solid var(--sar-line)", padding: "8px 12px" }}>
              <div style={{
                fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: ".06em", color: "var(--sar-text-3)", marginBottom: 6,
              }}>
                Resultados de esta sesión
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {finalizadas.slice(0, 5).map((t) => (
                  <li key={t.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "3px 0" }}>
                    <span
                      aria-hidden
                      style={{
                        marginTop: 6, width: 8, height: 8, borderRadius: 4,
                        flexShrink: 0, background: estiloTarea(t),
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{t.label}</div>
                      {t.detail && (
                        <div style={{ fontSize: 11, color: "var(--sar-text-3)" }}>{t.detail}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {listQuery.isLoading && (
            <div style={{ padding: 12, fontSize: 12, color: "var(--sar-text-3)" }}>
              Cargando…
            </div>
          )}
          {listQuery.data && listQuery.data.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: "var(--sar-text-3)" }}>
              No tienes notificaciones.
            </div>
          )}
          {listQuery.data && listQuery.data.length > 0 && (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {listQuery.data.map((n) => (
                <li
                  key={n.id}
                  style={{
                    borderBottom: "1px solid var(--sar-line)",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onClickItem(n)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      background: n.read_at ? "transparent" : "var(--sar-surface-2)",
                      border: "0",
                      padding: "10px 12px",
                      cursor: "pointer",
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        marginTop: 6,
                        width: 8,
                        height: 8,
                        borderRadius: 4,
                        flexShrink: 0,
                        background: severityColor(n.severity),
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          alignItems: "baseline",
                        }}
                      >
                        <strong
                          style={{
                            fontSize: 13,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {n.title}
                        </strong>
                        <small
                          style={{
                            fontSize: 11,
                            color: "var(--sar-text-3)",
                            flexShrink: 0,
                          }}
                        >
                          {formatRelative(n.created_at)}
                        </small>
                      </div>
                      {n.message && (
                        <p
                          style={{
                            margin: "2px 0 0",
                            fontSize: 12,
                            color: "var(--sar-text-2)",
                          }}
                        >
                          {n.message}
                        </p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
